import { useEffect, useState } from "react";
import api from "../lib/api";
import { Building2, Users, Receipt, Wallet, AlertCircle, TrendingUp, BadgeCheck } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const TOOLTIP_STYLE = { borderRadius: 0, border: "1px solid #e2e8f0" };

const fmtWhen = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

function Metric({ label, value, sub, icon: Icon, accent, testid }) {
  return (
    <div className="bg-white border border-slate-200 p-6 flex flex-col justify-between min-h-[140px]" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">{label}</div>
        <div className={`h-8 w-8 flex items-center justify-center ${accent}`}><Icon className="h-4 w-4" /></div>
      </div>
      <div className="mt-4">
        <div className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900">{value}</div>
        {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
      </div>
    </div>
  );
}

export default function Overview() {
  const [stats, setStats] = useState(null);
  const [payments, setPayments] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => setStats({}));
    api.get("/dashboard/recent-payments").then((r) => setPayments(r.data)).catch(() => setPayments([]));
  }, []);

  if (!stats) return <div data-testid="overview-loading" className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-8" data-testid="dashboard-overview">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Overview</div>
        <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tighter mt-1">Your portfolio at a glance</h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric testid="metric-pending" label="Pending rent" value={fmt(stats.pending_total)}
                sub={`${stats.pending_count || 0} invoice(s) outstanding`}
                icon={Receipt} accent="bg-red-50 text-red-600 border border-red-100" />
        <Metric testid="metric-collected" label="Collected" value={fmt(stats.paid_total)}
                sub="All-time paid invoices"
                icon={TrendingUp} accent="bg-green-50 text-green-700 border border-green-100" />
        <Metric testid="metric-expenses" label="Expenses" value={fmt(stats.expense_total)}
                sub="All-time spend"
                icon={Wallet} accent="bg-slate-100 text-slate-700 border border-slate-200" />
        <Metric testid="metric-occupancy" label="Occupancy" value={`${stats.occupancy_rate || 0}%`}
                sub={`${stats.occupied || 0} of ${stats.capacity || 0} beds`}
                icon={Users} accent="bg-blue-50 text-blue-700 border border-blue-100" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Metric testid="metric-properties" label="Properties" value={stats.properties || 0}
                icon={Building2} accent="bg-slate-100 text-slate-700 border border-slate-200" />
        <Metric testid="metric-tenants" label="Active tenants" value={stats.active_tenants || 0}
                icon={Users} accent="bg-slate-100 text-slate-700 border border-slate-200" />
        <Metric testid="metric-complaints" label="Open complaints" value={stats.open_complaints || 0}
                icon={AlertCircle} accent="bg-orange-50 text-orange-700 border border-orange-100" />
      </div>

      {/* ---------- Recent payments ---------- */}
      <div className="bg-white border border-slate-200" data-testid="recent-payments">
        <div className="px-6 py-5 border-b border-slate-200 flex items-center gap-2">
          <BadgeCheck className="h-5 w-5 text-green-600" />
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Activity</div>
            <h2 className="font-display text-2xl font-bold tracking-tight">Recent successful payments</h2>
          </div>
        </div>
        {payments.length === 0 ? (
          <div className="p-8 text-sm text-slate-500" data-testid="recent-payments-empty">
            No payments yet — successful tenant payments will appear here with tenant, PG, room and amount.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {payments.map((p) => (
              <li key={p.id} className="px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                  data-testid={`payment-${p.id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{p.tenant_name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {p.property_name} · Room {p.room_number} · Rent {p.period}
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">
                    {fmtWhen(p.paid_at)}
                    {p.transaction_id ? <span className="font-mono"> · TXN {p.transaction_id}</span> : null}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-xs uppercase tracking-wider bg-slate-100 text-slate-600 px-2 py-0.5">
                    {p.payment_method}
                  </span>
                  <span className="font-mono font-semibold text-green-700">{fmt(p.amount)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-white border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Trend</div>
            <h2 className="font-display text-2xl font-bold tracking-tight">Income vs pending due</h2>
          </div>
        </div>
        <div className="h-72 min-h-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={stats.monthly || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="period" stroke="#64748b" fontSize={12} />
              <YAxis stroke="#64748b" fontSize={12} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Legend />
              <Bar dataKey="income" fill="#16a34a" name="Income" />
              <Bar dataKey="pending" fill="#ea580c" name="Pending due" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
